import { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Text,
  Divider,
  FormControl,
  FormErrorMessage,
  HStack,
  Heading,
  Image,
  Input,
  Select as ChakraSelect,
  Radio,
  RadioGroup,
  VStack,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Select from 'react-select';

import countryCodes, { CountryProperty } from 'country-codes-list';
import { Controller } from 'react-hook-form';

import {
  useGetCompanySizeQuery,
  useGetCompanyCategoryQuery,
  useCreateBasicInfoMutation,
  useBasicInfoQuery,
  useUpdateBasicInfoMutation,
} from '../../../services';

import { ReactComponent as TrashIcon } from '../../../assets/icons/trash.svg';
import UploadIcon from '../../../assets/icons/upload.svg';
import { BasicInfoForm } from '../models';
import { CenteredSpinner } from '../components';
import {
  calculateFileSize,
  getApiErrorMessage,
  getBodyFailureMessage,
} from '../utils';

export const BasicInfo: FC = () => {
  const params = useParams();
  const { companyId } = params;
  const status = params.status || 'all';

  const { data: basicInfo, isLoading } = useBasicInfoQuery(Number(companyId), {
    skip: !companyId,
  });

  const [companyLogo, setCompanyLogo] = useState({ name: '', size: 0 });
  const [taxCardDocument, setTaxCardDocument] = useState({ name: '', size: 0 });
  const [commercialRecordDocument, setCommercialRecordDocument] = useState({
    name: '',
    size: 0,
  });

  const { data: companyCategories } = useGetCompanyCategoryQuery();
  const { data: companySizes } = useGetCompanySizeQuery();
  const [createBasicInfo] = useCreateBasicInfoMutation();
  const [updateBasicInfo] = useUpdateBasicInfoMutation();

  const toast = useToast();
  const navigate = useNavigate();

  const showSaveError = (description: string) =>
    toast({
      title: 'Could not save company',
      description,
      status: 'error',
      duration: 9000,
      isClosable: true,
    });

  const showSaveSuccess = () =>
    toast({
      title: companyId ? 'Company updated' : 'Company created',
      status: 'success',
      duration: 4000,
      isClosable: true,
    });

  const options = useMemo(
    () =>
      countryCodes.all().map(country => ({
        name: country.countryNameEn,
      })),
    []
  );

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    resetField,
    control,
    reset,
  } = useForm<BasicInfoForm>({
    defaultValues: {
      companyType: companyCategories?.data?.[0]?.id,
      companySize: String(companySizes?.data?.[0]?.id || 1),
    },
  });

  useEffect(() => {
    const resetBasicInfo = () => {
      const {
        companyName,
        companyType,
        numberOfProjects,
        taxID,
        countryList,
        companySize,
        companyLogoSize,
        taxCardDocumentSize,
        commercialRecordDocumentSize,
        companyLogoFileName,
        commercialRecordDocumentFileName,
        taxCardDocumentFileName,
      } = basicInfo.data;

      reset({
        companyName,
        // Backend Company.numberOfProjects is typed string; form field is number.
        numberOfProjects: Number(numberOfProjects),
        taxID,
        countryList,
        companySize: String(companySize.id),
        companyType: companyType.id,
      });

      setCompanyLogo({
        name: companyLogoFileName.split('/')[1],
        size: Number(companyLogoSize),
      });

      if (commercialRecordDocumentFileName) {
        setCommercialRecordDocument({
          name: commercialRecordDocumentFileName.split('/')[1],
          size: Number(commercialRecordDocumentSize),
        });
      }

      if (taxCardDocumentFileName) {
        setCommercialRecordDocument({
          name: taxCardDocumentFileName.split('/')[1],
          size: Number(taxCardDocumentSize),
        });
      }
    };

    if (companyId && basicInfo?.data) void resetBasicInfo();
  }, [basicInfo?.data, companyId, isLoading, reset]);

  const navigateToPreviousPage = (companyId = 'new-company') =>
    navigate(`/companies/${status}/${companyId}`);

  async function onSubmit(data: BasicInfoForm) {
    const {
      commercialRecordDocument,
      companyLogo,
      companyName,
      companySize,
      companyType,
      countryList,
      numberOfProjects,
      taxCardDocument,
      taxID,
    } = data;

    const formData = new FormData();
    if (companyLogo[0]) formData.append('companyLogo', companyLogo[0]);
    if (taxCardDocument[0])
      formData.append('taxCardDocument', taxCardDocument[0]);
    if (commercialRecordDocument[0])
      formData.append('commercialRecordDocument', commercialRecordDocument[0]);

    const basicInfoData = {
      companyName,
      companyType: { id: companyType },
      taxID,
      companySize: { id: +companySize },
      numberOfProjects,
      countryList,
    };

    if (companyId) basicInfoData['id'] = companyId;

    formData.append('basicInfo', JSON.stringify(basicInfoData));

    try {
      let savedCompanyId: string;

      if (companyId) {
        const updated = await updateBasicInfo(formData).unwrap();
        // core-api returns its AppResponse envelope with HTTP 200 even on
        // business failures, so `.unwrap()` resolves on them: classify the
        // envelope (errorCode, canonical — see src/utils/api-response.ts)
        // before treating it as success. Body failures now reach this same
        // error toast, which only transport failures used to reach.
        const failure = getBodyFailureMessage(updated);
        if (failure) {
          showSaveError(failure);
          return;
        }
        savedCompanyId = companyId;
      } else {
        const createdBasicInfo = await createBasicInfo(formData).unwrap();
        const failure = getBodyFailureMessage(createdBasicInfo);
        if (failure) {
          showSaveError(failure);
          return;
        }
        savedCompanyId = String(createdBasicInfo.data.id);
      }
      showSaveSuccess();
      navigateToPreviousPage(savedCompanyId);
    } catch (error) {
      console.error(error);
      showSaveError(getApiErrorMessage(error));
    }
  }

  if (isLoading) return <CenteredSpinner />;

  return (
    <Box>
      <HStack justify="space-between" mt="4" mb="12" align="start">
        <Heading color="custom_blue.1" fontSize="2xl">
          Basic Info
        </Heading>
      </HStack>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Company Name
            </Box>
            <Box fontSize="xs">This will be displayed on your profile.</Box>
          </VStack>
          <FormControl isInvalid={!!errors.companyName} flexBasis="50%" mt="2">
            <Input
              placeholder="Company Name"
              {...register('companyName', {
                required: 'Company Name is required',
              })}
            />
            <FormErrorMessage>
              {errors.companyName && errors.companyName.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>

        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Company Logo
            </Box>
            <Box fontSize="xs">
              Update your company logo and then choose where you want it to
              display.
            </Box>
          </VStack>

          <Box flexBasis="50%">
            <Box>
              <Input
                display="none"
                id="companyLogo"
                onInput={e => {
                  const [file] = (e.target as HTMLInputElement).files;
                  const { name, size } = file;

                  setCompanyLogo({ name, size });
                }}
                type="file"
                accept=".png, .jpg"
                {...register('companyLogo', {
                  required: companyId ? false : 'Company logo is required',
                })}
              />

              <label htmlFor="companyLogo">
                <VStack
                  cursor="pointer"
                  border="2px"
                  borderRadius="md"
                  borderStyle="dashed"
                  borderColor="custom_orange.2"
                  py="3"
                >
                  <Box
                    border="1px"
                    borderColor="custom_gray.4"
                    borderRadius="md"
                    p="2"
                  >
                    <Image src={UploadIcon} />
                  </Box>

                  <Text color="custom_blue.1" fontSize="sm">
                    <Box
                      as="span"
                      color="custom_orange.2"
                      fontWeight="semibold"
                    >
                      Click to upload
                    </Box>{' '}
                    or drag and drop
                  </Text>
                  <Text color="custom_blue.1" fontSize="xs">
                    PNG, JPG
                  </Text>
                </VStack>
              </label>

              {companyLogo.name && (
                <VStack
                  align="start"
                  border="1px"
                  borderRadius="md"
                  borderColor="custom_gray.4"
                  p="3"
                  mt="3"
                >
                  <HStack justify="space-between" w="full">
                    <Box
                      fontWeight="medium"
                      color="custom_blue.1"
                      fontSize="sm"
                    >
                      {companyLogo.name}
                    </Box>

                    <IconButton
                      icon={<TrashIcon />}
                      aria-label="delete uploaded company logo"
                      bg="transparent"
                      onClick={() => {
                        setCompanyLogo({ name: '', size: 0 });
                        resetField('companyLogo', {
                          keepDirty: true,
                          keepError: true,
                          keepTouched: true,
                        });
                      }}
                    />
                  </HStack>
                  <Box color="custom_blue.1" fontSize="sm">
                    {calculateFileSize(companyLogo.size)} MB
                  </Box>
                </VStack>
              )}
            </Box>

            <FormErrorMessage>
              {errors.companyLogo && errors.companyLogo.message}
            </FormErrorMessage>
          </Box>
        </HStack>

        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Company Type
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.companyType} flexBasis="50%" mt="2">
            <ChakraSelect
              {...register('companyType', {
                required: 'Company type is required',
              })}
            >
              <option />
              {companyCategories?.data.map(({ type, id }) => (
                <option key={id} value={id}>
                  {type}
                </option>
              ))}
            </ChakraSelect>
            <FormErrorMessage>
              {errors.companyType && errors.companyType.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>

        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Company TAX-ID{' '}
              <Box fontSize="xs" as="span">
                (Optional)
              </Box>
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.taxID} flexBasis="50%" mt="2">
            <Input placeholder="TAX-ID" {...register('taxID')} />
          </FormControl>
        </HStack>

        <Divider />
        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Commercial Record Doc{' '}
              <Box fontSize="xs" as="span">
                (Optional)
              </Box>
            </Box>
          </VStack>

          <Box flexBasis="50%">
            <Box>
              <Input
                display="none"
                id="commercialRecordDocument"
                onInput={e => {
                  const [file] = (e.target as HTMLInputElement).files;
                  const { name, size } = file;

                  setCommercialRecordDocument({ name, size });
                }}
                type="file"
                accept=".png, .jpg, .pdf"
                {...register('commercialRecordDocument')}
              />

              <label htmlFor="commercialRecordDocument">
                <VStack
                  cursor="pointer"
                  border="1px"
                  borderRadius="md"
                  borderColor="custom_gray.4"
                  py="3"
                >
                  <Box
                    border="1px"
                    borderColor="custom_gray.4"
                    borderRadius="md"
                    p="2"
                  >
                    <Image src={UploadIcon} />
                  </Box>

                  <Text color="custom_blue.1" fontSize="sm">
                    <Box
                      as="span"
                      color="custom_orange.2"
                      fontWeight="semibold"
                    >
                      Click to upload
                    </Box>{' '}
                    or drag and drop
                  </Text>
                  <Text color="custom_blue.1" fontSize="xs">
                    PNG, JPG or PDF
                  </Text>
                </VStack>
              </label>

              {commercialRecordDocument.name && (
                <VStack
                  align="start"
                  border="1px"
                  borderRadius="md"
                  borderColor="custom_gray.4"
                  p="3"
                  mt="3"
                >
                  <HStack justify="space-between" w="full">
                    <Box
                      fontWeight="medium"
                      color="custom_blue.1"
                      fontSize="sm"
                    >
                      {commercialRecordDocument.name}
                    </Box>

                    <IconButton
                      icon={<TrashIcon />}
                      aria-label="delete uploaded company logo"
                      bg="transparent"
                      onClick={() => {
                        setCommercialRecordDocument({ name: '', size: 0 });
                        resetField('commercialRecordDocument', {
                          keepDirty: true,
                          keepError: true,
                          keepTouched: true,
                        });
                      }}
                    />
                  </HStack>
                  <Box color="custom_blue.1" fontSize="sm">
                    {calculateFileSize(commercialRecordDocument.size)} MB
                  </Box>
                </VStack>
              )}
            </Box>
          </Box>
        </HStack>

        <Divider />
        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Tax Card Doc{' '}
              <Box fontSize="xs" as="span">
                (Optional)
              </Box>
            </Box>
          </VStack>

          <Box flexBasis="50%">
            <Box>
              <Input
                display="none"
                id="taxCardDocument"
                onInput={e => {
                  const [file] = (e.target as HTMLInputElement).files;
                  const { name, size } = file;

                  setTaxCardDocument({ name, size });
                }}
                type="file"
                accept=".png, .jpg, .pdf"
                {...register('taxCardDocument')}
              />

              <label htmlFor="taxCardDocument">
                <VStack
                  cursor="pointer"
                  border="1px"
                  borderRadius="md"
                  borderColor="custom_gray.4"
                  py="3"
                >
                  <Box
                    border="1px"
                    borderColor="custom_gray.4"
                    borderRadius="md"
                    p="2"
                  >
                    <Image src={UploadIcon} />
                  </Box>

                  <Text color="custom_blue.1" fontSize="sm">
                    <Box
                      as="span"
                      color="custom_orange.2"
                      fontWeight="semibold"
                    >
                      Click to upload
                    </Box>{' '}
                    or drag and drop
                  </Text>
                  <Text color="custom_blue.1" fontSize="xs">
                    PNG, JPG or PDF
                  </Text>
                </VStack>
              </label>

              {taxCardDocument.name && (
                <VStack
                  align="start"
                  border="1px"
                  borderRadius="md"
                  borderColor="custom_gray.4"
                  p="3"
                  mt="3"
                >
                  <HStack justify="space-between" w="full">
                    <Box
                      fontWeight="medium"
                      color="custom_blue.1"
                      fontSize="sm"
                    >
                      {taxCardDocument.name}
                    </Box>

                    <IconButton
                      icon={<TrashIcon />}
                      aria-label="delete uploaded company logo"
                      bg="transparent"
                      onClick={() => {
                        setTaxCardDocument({ name: '', size: 0 });
                        resetField('taxCardDocument', {
                          keepDirty: true,
                          keepError: true,
                          keepTouched: true,
                        });
                      }}
                    />
                  </HStack>
                  <Box color="custom_blue.1" fontSize="sm">
                    {calculateFileSize(taxCardDocument.size)} MB
                  </Box>
                </VStack>
              )}
            </Box>
          </Box>
        </HStack>

        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Countries Size
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.companySize} flexBasis="70%">
            <Controller
              control={control}
              name="companySize"
              rules={{ required: 'Company size is required' }}
              render={({ field }) => (
                <FormControl>
                  <RadioGroup {...field}>
                    {companySizes?.data.map(({ id, size }) => (
                      <Radio
                        value={String(id)}
                        key={id}
                        mr="4"
                        colorScheme="orange"
                      >
                        {size} Employees
                      </Radio>
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />
            <FormErrorMessage>
              {errors.companySize && errors.companySize.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Projects Number
            </Box>
          </VStack>
          <FormControl
            isInvalid={!!errors.numberOfProjects}
            flexBasis="50%"
            mt="2"
          >
            <Input
              placeholder="Projects Number"
              {...register('numberOfProjects', {
                required: 'Company size is required',
              })}
            />
            <FormErrorMessage>
              {errors.numberOfProjects && errors.numberOfProjects.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>

        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Countries List
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.countryList} flexBasis="50%" mt="2">
            <Controller
              control={control}
              name="countryList"
              rules={{
                required: 'Country list is required',
              }}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="Select Countries List"
                  options={options}
                  isMulti
                  getOptionLabel={({ name }) => name}
                  formatOptionLabel={({ name }) => {
                    const country = countryCodes.findOne(
                      'countryNameEn' as CountryProperty,
                      name
                    );

                    return (
                      <div className="flex items-center">
                        <span
                          className={`fi fi-${country.countryCode.toLowerCase()} fis`}
                        ></span>
                        <span
                          style={{
                            marginLeft: 5,
                            width: '80px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {name}
                        </span>
                      </div>
                    );
                  }}
                />
              )}
            />
            <FormErrorMessage>
              {errors.countryList && errors.countryList.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>

        <HStack mt={4} justify="end">
          <Button onClick={() => navigateToPreviousPage(companyId)}>
            Cancel
          </Button>
          <Button
            isLoading={isSubmitting}
            type="submit"
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
          >
            Save
          </Button>
        </HStack>
      </form>
    </Box>
  );
};
